import * as React from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FilePicker,
  toast,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';
import { dashboardT } from '../../../../../lib/messages';
import { useBlockedListMutations } from '../../hooks';
import { BlockedListFileFormat } from '../../model';

export function BlockedListUploadDialog({
  format,
  onClose,
}: {
  format: BlockedListFileFormat;
  onClose: () => void;
}) {
  const [file, setFile] = React.useState<File | null>(null);
  const [submitted, setSubmitted] = React.useState(false);
  const mutations = useBlockedListMutations();
  const guard = useBusyDialogGuard(mutations.upload.isPending, onClose);
  const formatLabel = format === BlockedListFileFormat.Csv ? 'CSV' : 'XLSX';
  const error = submitted && !file ? dashboardT('blockedList.fileRequired') : undefined;

  async function upload() {
    setSubmitted(true);
    if (!file) return;
    try {
      await mutations.upload.mutateAsync({ format, file });
      toast.success(dashboardT('blockedList.uploaded'));
      onClose();
    } catch (error) {
      showRequestError(error, dashboardT('blockedList.uploadError'));
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        guard.onOpenChange(open);
      }}
    >
      <DialogContent
        className="max-w-[520px] gap-0 p-0"
        onEscapeKeyDown={(event) => {
          guard.onDismissInteraction(event);
        }}
        onPointerDownOutside={(event) => {
          guard.onDismissInteraction(event);
        }}
      >
        <DialogHeader className="border-border border-b px-5 py-3.5">
          <DialogTitle className="text-[15px] font-semibold">
            {dashboardT('blockedList.uploadTitle')}
          </DialogTitle>
          <DialogDescription>
            {dashboardT('blockedList.uploadDescription', { format: formatLabel })}
          </DialogDescription>
        </DialogHeader>
        <div className="px-5 py-5">
          <FilePicker
            label={dashboardT('blockedList.file')}
            buttonLabel={dashboardT('blockedList.chooseFile')}
            accept={
              format === BlockedListFileFormat.Csv
                ? '.csv,text/csv'
                : '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }
            value={file}
            onFileChange={setFile}
            required
            disabled={mutations.upload.isPending}
            error={error}
          />
        </div>
        <DialogFooter className="border-border border-t px-5 py-3">
          <Button
            type="button"
            variant="outline"
            disabled={mutations.upload.isPending}
            onClick={() => {
              guard.close();
            }}
          >
            {dashboardT('blockedList.cancel')}
          </Button>
          <Button type="button" disabled={mutations.upload.isPending} onClick={() => void upload()}>
            {dashboardT('blockedList.upload')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
